import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import AdminOverview from "./admin/AdminOverview";
import AdminUsers from "./admin/AdminUsers";
import AdminModules from "./admin/AdminModules";
import AdminTerrain from "./admin/AdminTerrain";
import AdminAssociations from "./admin/AdminAssociations";
import AdminEngagement from "./admin/AdminEngagement";
import AdminSettings from "./admin/AdminSettings";
import AdminCandidates from "./admin/AdminCandidates";
import AdminRewards from "./admin/AdminRewards";
import AdminPayouts from "./admin/AdminPayouts";
import AdminNewsImport from "./admin/AdminNewsImport";

const TABS = [
    { id: "overview", label: "Vue d'ensemble" },
    { id: "users", label: "Utilisateurs" },
    { id: "candidates", label: "Inscriptions" },
    { id: "modules", label: "Modules" },
    { id: "terrain", label: "Terrain Rapide" },
    { id: "associations", label: "Associations (formation)" },
    { id: "news-import", label: "Import actu (Lia)" },
    { id: "engagement", label: "Engagement" },
    { id: "rewards", label: "Récompenses" },
    { id: "payouts", label: "Paiements" },
    { id: "settings", label: "Paramètres" },
];

export default function Admin() {
    // Iter-27.14: deep-linking — `?tab=payouts` opens the Paiements tab so
    // that clicking a "payout claimed" notification lands on the right view.
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");
    const initialTab = TABS.find((t) => t.id === tabFromUrl)?.id || "overview";
    const [tab, setTab] = useState(initialTab);
    // Iter-34 — badge "inscriptions en attente" sur l'onglet Inscriptions
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        api.get("/admin/candidates?status=pending")
            .then(({ data }) => setPendingCount(data.length))
            .catch(() => {});
    }, [tab]);

    useEffect(() => {
        // Sync URL when user clicks a tab manually (without a full reload)
        const current = searchParams.get("tab");
        if (tab !== current) {
            const next = new URLSearchParams(searchParams);
            if (tab === "overview") {
                next.delete("tab");
            } else {
                next.set("tab", tab);
            }
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    useEffect(() => {
        // External URL change (notification click) -> sync the tab state
        const fromUrl = searchParams.get("tab");
        if (fromUrl && fromUrl !== tab && TABS.find((t) => t.id === fromUrl)) {
            setTab(fromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    return (
        <Layout title="Backoffice" subtitle="Pilotage de l'académie">
            <div
                className="mb-6 overflow-x-auto scrollbar-hide -mx-1 px-1"
                data-testid="admin-tabs-scroll"
            >
                <div
                    className="flex gap-2 p-1.5 glass-card rounded-full w-max"
                    data-testid="admin-tabs"
                >
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`px-4 lg:px-5 py-2 rounded-full text-xs lg:text-sm font-bold whitespace-nowrap transition-all ${
                                tab === t.id
                                    ? "bg-shariti-orange text-white"
                                    : "text-white/60 hover:text-white"
                            }`}
                        >
                            {t.label}
                            {t.id === "candidates" && pendingCount > 0 && (
                                <span
                                    data-testid="pending-signups-badge"
                                    className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-shariti-yellow text-black text-[10px] font-extrabold align-middle"
                                >
                                    {pendingCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {tab === "overview" && <AdminOverview />}
            {tab === "users" && <AdminUsers />}
            {tab === "candidates" && <AdminCandidates />}
            {tab === "modules" && <AdminModules />}
            {tab === "terrain" && <AdminTerrain />}
            {tab === "associations" && <AdminAssociations />}
            {tab === "news-import" && <AdminNewsImport />}
            {tab === "engagement" && <AdminEngagement />}
            {tab === "rewards" && <AdminRewards />}
            {tab === "payouts" && <AdminPayouts />}
            {tab === "settings" && <AdminSettings />}
        </Layout>
    );
}
